import { createServerFn } from "@tanstack/react-start";
import { and, desc, eq, isNull } from "drizzle-orm";
import { nanoid } from "nanoid";
import { z } from "zod";
import {
  citationTags,
  projectCitations,
  projectScreeningSettings,
  projectTags,
  screeningDecisions,
} from "~/lib/db/schema";

const projectIdSchema = z.object({
  projectId: z.string(),
});

const citationIdSchema = z.object({
  projectId: z.string(),
  citationId: z.string(),
});

const createTagSchema = z.object({
  projectId: z.string(),
  name: z.string().min(1).max(50),
});

const addTagToCitationSchema = z.object({
  projectId: z.string(),
  citationId: z.string(),
  tagId: z.string(),
});

const removeTagFromCitationSchema = z.object({
  projectId: z.string(),
  citationId: z.string(),
  tagId: z.string(),
});

export interface ProjectTag {
  id: string;
  name: string;
  usageCount: number;
}

export interface CitationTag {
  id: string;
  tagId: string;
  name: string;
  createdBy: string | null;
  createdByName: string | null;
  createdAt: string;
}

async function getCitationInProject(
  db: typeof import("~/lib/db").db,
  projectId: string,
  citationId: string,
) {
  const citation = await db.query.projectCitations.findFirst({
    where: and(
      eq(projectCitations.projectId, projectId),
      eq(projectCitations.id, citationId),
    ),
    columns: {
      id: true,
      screeningPhase: true,
    },
  });

  if (!citation) {
    throw new Error("Citation not found");
  }

  return citation;
}

async function assertTagInProject(
  db: typeof import("~/lib/db").db,
  projectId: string,
  tagId: string,
) {
  const tag = await db.query.projectTags.findFirst({
    where: and(eq(projectTags.id, tagId), eq(projectTags.projectId, projectId)),
    columns: { id: true },
  });

  if (!tag) {
    throw new Error("Tag not found");
  }
}

async function shouldHidePeerTags(
  db: typeof import("~/lib/db").db,
  projectId: string,
  citationId: string,
  userId: string,
) {
  const citation = await getCitationInProject(db, projectId, citationId);
  const settings = await db.query.projectScreeningSettings.findFirst({
    where: eq(projectScreeningSettings.projectId, projectId),
    columns: {
      screeningMode: true,
      blindReviewers: true,
    },
  });

  if (
    !settings ||
    settings.screeningMode !== "dual" ||
    !settings.blindReviewers
  ) {
    return false;
  }

  const myDecision = await db.query.screeningDecisions.findFirst({
    where: and(
      eq(screeningDecisions.projectCitationId, citationId),
      eq(screeningDecisions.reviewerId, userId),
      eq(screeningDecisions.phase, citation.screeningPhase),
      isNull(screeningDecisions.deletedAt),
    ),
    columns: { id: true },
  });

  return !myDecision;
}

/**
 * Get all tags for a project with usage counts
 */
export const $getProjectTags = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => projectIdSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    const tags = await db.query.projectTags.findMany({
      where: eq(projectTags.projectId, data.projectId),
      with: {
        citationTags: true,
      },
      orderBy: [desc(projectTags.createdAt)],
    });

    return tags.map((t) => ({
      id: t.id,
      name: t.name,
      usageCount: t.citationTags.length,
    })) satisfies ProjectTag[];
  });

/**
 * Get frequently used tags for suggestions
 */
export const $getSuggestedTags = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => projectIdSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    const tags = await db.query.projectTags.findMany({
      where: eq(projectTags.projectId, data.projectId),
      with: {
        citationTags: true,
      },
    });

    // Sort by usage count and return top 6
    return tags
      .map((t) => ({
        id: t.id,
        name: t.name,
        usageCount: t.citationTags.length,
      }))
      .sort((a, b) => b.usageCount - a.usageCount)
      .slice(0, 6) satisfies ProjectTag[];
  });

/**
 * Get tags for a specific citation
 */
export const $getCitationTags = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => citationIdSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);
    const hidePeerTags = await shouldHidePeerTags(
      db,
      data.projectId,
      data.citationId,
      user.id,
    );

    const tags = await db.query.citationTags.findMany({
      where: eq(citationTags.projectCitationId, data.citationId),
      with: {
        tag: true,
        createdByUser: true,
      },
    });

    const visibleTags = hidePeerTags
      ? tags.filter((tag) => tag.createdBy === user.id)
      : tags;

    return visibleTags.map((ct) => ({
      id: ct.id,
      tagId: ct.tag.id,
      name: ct.tag.name,
      createdBy: ct.createdBy,
      createdByName: ct.createdByUser?.name ?? null,
      createdAt: ct.createdAt.toISOString(),
    })) satisfies CitationTag[];
  });

/**
 * Create a new tag in the project
 */
export const $createProjectTag = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => createTagSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    // Check if tag already exists
    const existing = await db.query.projectTags.findFirst({
      where: and(
        eq(projectTags.projectId, data.projectId),
        eq(projectTags.name, data.name.trim()),
      ),
    });

    if (existing) {
      return { id: existing.id, name: existing.name, usageCount: 0 } satisfies ProjectTag;
    }

    const id = nanoid();
    await db.insert(projectTags).values({
      id,
      projectId: data.projectId,
      name: data.name.trim(),
    });

    return { id, name: data.name.trim(), usageCount: 0 } satisfies ProjectTag;
  });

/**
 * Add a tag to a citation
 */
export const $addTagToCitation = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => addTagToCitationSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);
    await getCitationInProject(db, data.projectId, data.citationId);
    await assertTagInProject(db, data.projectId, data.tagId);

    // Check if already applied
    const existing = await db.query.citationTags.findFirst({
      where: and(
        eq(citationTags.projectCitationId, data.citationId),
        eq(citationTags.tagId, data.tagId),
      ),
    });

    if (existing) {
      return { success: true, alreadyExists: true };
    }

    const id = nanoid();
    await db.insert(citationTags).values({
      id,
      projectCitationId: data.citationId,
      tagId: data.tagId,
      createdBy: user.id,
    });

    return { success: true, alreadyExists: false };
  });

/**
 * Remove a tag from a citation
 */
export const $removeTagFromCitation = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => removeTagFromCitationSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);
    await getCitationInProject(db, data.projectId, data.citationId);
    await assertTagInProject(db, data.projectId, data.tagId);

    // Check ownership before removing
    const existing = await db.query.citationTags.findFirst({
      where: and(
        eq(citationTags.projectCitationId, data.citationId),
        eq(citationTags.tagId, data.tagId),
      ),
    });

    if (existing && existing.createdBy && existing.createdBy !== user.id) {
      throw new Error("You can only remove tags you created");
    }

    await db
      .delete(citationTags)
      .where(
        and(
          eq(citationTags.projectCitationId, data.citationId),
          eq(citationTags.tagId, data.tagId),
        ),
      );

    return { success: true };
  });

/**
 * Create a tag and immediately add it to a citation
 */
export const $createAndAddTag = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z
      .object({
        projectId: z.string(),
        citationId: z.string(),
        tagName: z.string().min(1).max(50),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);
    await getCitationInProject(db, data.projectId, data.citationId);

    // Check if tag already exists
    let tag = await db.query.projectTags.findFirst({
      where: and(
        eq(projectTags.projectId, data.projectId),
        eq(projectTags.name, data.tagName.trim()),
      ),
    });

    if (!tag) {
      const tagId = nanoid();
      await db.insert(projectTags).values({
        id: tagId,
        projectId: data.projectId,
        name: data.tagName.trim(),
      });
      tag = {
        id: tagId,
        name: data.tagName.trim(),
        projectId: data.projectId,
        createdAt: new Date(),
      };
    }

    // Check if already applied to citation
    const existingLink = await db.query.citationTags.findFirst({
      where: and(
        eq(citationTags.projectCitationId, data.citationId),
        eq(citationTags.tagId, tag.id),
      ),
    });

    if (!existingLink) {
      await db.insert(citationTags).values({
        id: nanoid(),
        projectCitationId: data.citationId,
        tagId: tag.id,
        createdBy: user.id,
      });
    }

    return { id: tag.id, name: tag.name } satisfies { id: string; name: string };
  });
